import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Quote, 
  ArrowRight,
  Loader2,
  Star
} from 'lucide-react';
import { getSuccessStories } from '../services/apiService';

const IELTSSuccessStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await getSuccessStories();
        // Filter for IELTS stories only
        const ieltsStories = data.filter(
          (s) => s.category === 'ielts'
        );
        // Filter out stories with blob URLs
        const validStories = ieltsStories.filter(story => {
          const url = story.photo || story.photo_url || story.imageUrl;
          return !url || !url.startsWith('blob:');
        });
        setStories(validStories);
      } catch (error) {
        console.error("Failed to load IELTS stories", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Extract unique course/program names for filtering
  const courses = ['All', ...new Set(stories.map(s => s.course || 'General').filter(Boolean))];

  const filteredStories = filter === 'All' 
    ? stories 
    : stories.filter(s => (s.course || 'General') === filter);

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      
      {/* =========================================
          HERO SECTION
      ========================================= */}
      <section className="relative pt-24 md:pt-32 pb-16 md:pb-20 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-gradient-to-br from-blue-900/20 to-purple-900/20"></div>
        <div className="absolute top-0 right-0 w-full h-full bg-blue-900/10 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 md:px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 px-3 py-1 md:px-4 md:py-1.5 rounded-full text-xs md:text-sm font-bold uppercase tracking-wider mb-4 md:mb-6 mt-5">
              <Award size={14} className="md:w-4 md:h-4" /> Top IELTS Scorers
            </div>
            
            <h1 className="text-3xl md:text-5xl text-gray-100 font-serif font-bold mb-4 md:mb-6 leading-tight">
              IELTS <span className="text-blue-500">Success Stories</span>
            </h1>
            
            <p className="text-base md:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed px-2">
              From Band 6.5 to Band 9.0 - Real Stories of IELTS Excellence
            </p>
          </motion.div>
        </div>
      </section>

      {/* =========================================
          FILTER BAR (Sticky & Mobile Scrollable)
      ========================================= */}
      <div className="top-16 md:top-20 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 py-3 md:py-4 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 relative">
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent md:hidden pointer-events-none z-10" />
          
          <div className="flex flex-nowrap overflow-x-auto no-scrollbar gap-2 md:justify-center pb-1 snap-x scroll-pl-4">
            {courses.map(course => (
              <button
                key={course}
                onClick={() => setFilter(course)}
                className={`snap-start px-4 py-2 md:px-5 md:py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all flex-shrink-0 border ${
                  filter === course 
                    ? 'bg-slate-900 border-slate-900 text-white shadow-md' 
                    : 'bg-white border-slate-200 text-slate-600 hover:border-blue-500 hover:text-blue-600'
                }`}
              >
                {course}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* =========================================
          STORIES GRID
      ========================================= */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-blue-600" size={48} />
            </div>
          ) : (
            <motion.div 
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
            >
              <AnimatePresence>
                {filteredStories.length > 0 ? filteredStories.map((story) => {
                  const photoUrl = story.photo || story.photo_url || story.imageUrl;
                  const safePhotoUrl = photoUrl && !photoUrl.startsWith('blob:') ? photoUrl : null;
                  
                  return (
                    <motion.div
                      layout
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      key={story.id}
                      className="bg-white rounded-xl md:rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group flex flex-col"
                    >
                      {/* Top: Profile Info */}
                      <div className="p-4 md:p-6 pb-0 flex items-start gap-3 md:gap-4">
                        <div className="w-12 h-12 md:w-16 md:h-16 rounded-full overflow-hidden border-2 border-slate-100 shadow-sm shrink-0 bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                          {safePhotoUrl ? (
                            <img src={safePhotoUrl} alt={story.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-blue-600 font-bold text-lg md:text-xl">{(story.name || 'S').charAt(0)}</span>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base md:text-lg font-bold text-slate-900 leading-tight truncate">{story.name || 'Student'}</h3>
                          <p className="text-xs md:text-sm text-slate-500 mt-1 line-clamp-1">{story.university || story.course || 'IELTS Student'}</p>
                          
                          {story.course && (
                            <div className="flex items-center gap-1 text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-wider mt-1.5 md:mt-2">
                              <BookOpen size={10} className="md:w-3 md:h-3 text-blue-500"/> {story.course}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Middle: Score/Result Info */}
                      <div className="p-4 md:p-6 flex-grow">
                        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg md:rounded-xl p-3 md:p-4 border border-blue-100 relative overflow-hidden h-full">
                          <div className="absolute -right-4 -bottom-4 text-blue-100 opacity-50 rotate-[-15deg] pointer-events-none">
                              <Award size={60} className="md:w-20 md:h-20" />
                          </div>
                          
                          <div className="relative z-10">
                            <div className="text-[10px] text-slate-400 uppercase font-bold tracking-widest mb-1">Achievement</div>
                            <div className="font-serif font-bold text-slate-900 text-base md:text-lg mb-2 md:mb-3 leading-tight line-clamp-2">
                              {story.course || 'IELTS Success'}
                            </div>
                            
                            <div className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 px-2 py-1 rounded text-[10px] md:text-xs font-bold border border-blue-200">
                              <Star size={10} className="md:w-3 md:h-3 fill-current" /> Top Scorer
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Bottom: Quote */}
                      <div className="px-4 md:px-6 pb-4 md:pb-6 mt-auto">
                        <div className="flex gap-2 mb-4">
                          <Quote size={16} className="text-slate-300 shrink-0 md:w-5 md:h-5" />
                          <p className="text-slate-600 italic text-xs md:text-sm leading-relaxed line-clamp-3">
                            {story.story || 'Great experience with 6th Sense Training Academy!'}
                          </p>
                        </div>
                        
                        {story.university && (
                          <div className="text-xs text-slate-500 font-medium">
                            {story.university}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                }) : (
                  <div className="col-span-full text-center py-20">
                    <Award size={48} className="mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">No IELTS success stories found.</p>
                  </div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* =========================================
          CTA: BOOK DEMO CLASS
      ========================================= */}
      <section className="py-16 md:py-24 bg-slate-900 text-white border-t border-slate-800">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-block p-3 md:p-4 rounded-full bg-blue-600/10 mb-4 md:mb-6">
            <Award size={24} className="md:w-8 md:h-8 text-blue-500 animate-pulse" />
          </div>
          <h2 className="text-2xl md:text-3xl text-gray-100 font-serif font-bold mb-4 md:mb-6">
            Your success story starts here.
          </h2>
          <p className="text-slate-400 text-base md:text-lg mb-8 md:mb-10 max-w-2xl mx-auto">
            Join hundreds of students who achieved their target IELTS scores with our proven training methods.
          </p>
          <Link
            to="/book-free-demo-class"
            className="w-full md:w-auto inline-flex justify-center items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-base md:text-lg font-bold transition-all hover:scale-105 shadow-xl shadow-blue-900/20"
          >
            Book Free Demo Class <ArrowRight size={20} />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default IELTSSuccessStories;

